import { MutableRefObject, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { styled } from "@monolith-forensics/monolith-ui";
import { CaseFilesProvider } from "@/Features/Files/Hooks/CaseFilesContext";
import type { CaseFilesTableRef } from "@/Features/Files/Tables/CaseFilesTable";
import { labAssetQueryKeys } from "@/Features/LabAssets/labAssets.queryKeys";
import { AssetAttachmentsAPI } from "../asset-attachments.api";
import { AssetAttachmentsTable } from "./AssetAttachmentsTable";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

/**
 * The asset's Attachments tab (ENG-2291): its attachments and its
 * validations' reports, shown as the Case Files table the way the evidence
 * item Files tab is. No case and no folders, so the provider gets the asset's
 * routes and the "asset-attachments" surface, which hides every case-only
 * control. Uploads go through the global uploader, so they keep going after
 * the tab or page changes. The parent keys this on the asset.
 */
export const AssetAttachmentsContent: React.FC<{ assetUuid: string }> = ({
  assetUuid,
}) => {
  const queryClient = useQueryClient();
  const tableRef = useRef<CaseFilesTableRef>();

  // The api logs an "Asset Attachment Added/Deleted" entry per upload and
  // delete; refresh History, which may be open when an upload finishes.
  const api = useMemo(
    () =>
      AssetAttachmentsAPI.assetFilesApi(assetUuid, () =>
        queryClient.invalidateQueries({
          queryKey: labAssetQueryKeys.activityLog(assetUuid),
        })
      ),
    [assetUuid, queryClient]
  );

  return (
    <StyledContainer>
      <CaseFilesProvider
        api={api}
        tableRef={tableRef as MutableRefObject<CaseFilesTableRef>}
        flat
        surface="asset-attachments"
      >
        <AssetAttachmentsTable assetUuid={assetUuid} />
      </CaseFilesProvider>
    </StyledContainer>
  );
};

export default AssetAttachmentsContent;
